import { useEffect, useState } from 'react';

import { Loading } from '@revelio/core';
import { useFirstLoadGateStore } from '@revelio/core/first-load-gate';

import styles from './first-load-overlay.module.css';

/**
 * Never leave the app stuck behind an opaque panel — one wedged query would
 * otherwise be indistinguishable from a hung dashboard. Matches the long-load
 * threshold `usePageLoadMonitor` already treats as pathological.
 */
const BAIL_OUT_MS = 10_000;
const FADE_MS = 200;

/**
 * How long the app has to stay free of loaders before the cover comes off.
 * A page hands off between loaders in stages — the page-level loader clears
 * before the charts mount, and a chart whose provider starts late can appear
 * a frame or two after its neighbours have settled. Confirming over a short
 * window instead of a single frame stops the reveal landing in one of those
 * gaps, which would put back the shift this exists to remove.
 */
const QUIET_PERIOD_MS = 150;

/**
 * Covers the whole viewport — sidebar and toolbar included — from the moment
 * auth clears until the first page has finished painting, so the loader the
 * user sees never moves between the three places it used to appear.
 *
 * An overlay rather than a gate on rendering: the app underneath has to mount
 * and fetch for the page to ever become ready. First load only — `isRevealed`
 * latches, so later navigations keep their in-place loaders.
 */
export const FirstLoadOverlay = () => {
  const blockerCount = useFirstLoadGateStore((state) => state.blockerCount);
  const isRevealed = useFirstLoadGateStore((state) => state.isRevealed);
  const reveal = useFirstLoadGateStore((state) => state.reveal);
  const [isMounted, setIsMounted] = useState(true);

  useEffect(() => {
    if (isRevealed) return;

    const timer = window.setTimeout(reveal, BAIL_OUT_MS);
    return () => window.clearTimeout(timer);
  }, [isRevealed, reveal]);

  // Re-check after a quiet period rather than revealing the moment the count
  // hits zero — see QUIET_PERIOD_MS. Any blocker arriving in the meantime
  // changes `blockerCount`, which cancels this timer through the cleanup.
  useEffect(() => {
    if (isRevealed || blockerCount > 0) return;

    const timer = window.setTimeout(() => {
      if (useFirstLoadGateStore.getState().blockerCount === 0) reveal();
    }, QUIET_PERIOD_MS);
    return () => window.clearTimeout(timer);
  }, [isRevealed, blockerCount, reveal]);

  useEffect(() => {
    if (!isRevealed) return;

    const timer = window.setTimeout(() => setIsMounted(false), FADE_MS);
    return () => window.clearTimeout(timer);
  }, [isRevealed]);

  if (!isMounted) return null;

  return (
    <div
      data-testid="first-load-overlay"
      className={styles.overlay}
      data-revealed={isRevealed || undefined}
    >
      {/* Blocking here would mean waiting on itself. */}
      <Loading blocksFirstLoad={false} />
    </div>
  );
};
